import { Button, Form, FormInstance, Input, InputNumber, Select, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FormButton, FormField, FormAttachment, FormTitle, FormTextArea } from '../form/FormBasic';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { useEffect, useRef, useState } from 'react';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER } from '../../constants/regex';
import { productService } from '../../api/services/productService';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  positiveNumberRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';

interface AddBasketFormProps {
  form: FormInstance;
  handleCreate: () => void;
  handleCancel: () => void;
  isModal?: boolean;
  getConfirmation?: () => void;
  isBasketModalOpen?: boolean;
  isEdit?: boolean;
  isEditDataLoading?: boolean;
}

interface ProductAssignmentItemProps {
  field: ProductAssignmentField;
  index: number;
  productOptions: MainProductOption[];
  assignedProductOptions: Record<number, ProductOption[]>;
  setAssignedProductOptions: React.Dispatch<React.SetStateAction<Record<number, ProductOption[]>>>;
  remove: (index: number | number[]) => void;
  fieldsLength: number;
  t: TFunction;
}

type SelectValue = string | number;

interface ProductAssignmentField {
  key: number;
  name: number;
}

interface MainProduct {
  id: number;
  refId?: SelectValue | null;
  name: string;
  unit?: string | null;
}

interface Product {
  id: number;
  refId?: SelectValue | null;
  name?: string;
  isActive?: boolean;
  mainProductId?: number;
  mainProduct?: {
    id?: number;
  } | null;
}

interface ProductOption {
  label: string;
  value: SelectValue;
  id: number;
  refId?: SelectValue | null;
}

interface MainProductOption extends ProductOption {
  unit?: string | null;
}

const getMainProductOptions = (mainProducts: MainProduct[]): MainProductOption[] =>
  mainProducts.map((mainProduct) => ({
    label: mainProduct.name,
    value: mainProduct.id,
    id: mainProduct.id,
    refId: mainProduct.refId,
    unit: mainProduct.unit,
  }));

const getAssignedProductOptions = (products: Product[] | { data?: Product[] }): ProductOption[] => {
  const productList = Array.isArray(products) ? products : (products?.data ?? []);

  return productList
    .filter((product) => product?.isActive !== false && Number.isFinite(Number(product?.id)))
    .map((product) => ({
      label: product.name ?? '',
      value: Number(product.id),
      id: Number(product.id),
      refId: product.refId,
    }));
};

const ProductAssignmentItem: React.FC<ProductAssignmentItemProps> = ({
  field,
  index,
  productOptions,
  assignedProductOptions,
  setAssignedProductOptions,
  remove,
  fieldsLength,
  t,
}) => {
  const form = Form.useFormInstance();
  const selectedMainProductValue = Form.useWatch(
    ['productAssignments', field.name, 'product'],
    form
  );
  const productAssignments = Form.useWatch('productAssignments', form) ?? [];
  const selectedUnit = Form.useWatch(['productAssignments', field.name, 'unit'], form);
  const unitOptions = selectedUnit ? [{ label: selectedUnit, value: selectedUnit }] : [];
  const previousMainProductValue = useRef<SelectValue | undefined>(undefined);
  const selectedMainProductsInOtherRows = new Set(
    productAssignments
      .map((assignment: { product?: SelectValue } | undefined, assignmentIndex: number) =>
        assignmentIndex === field.name ? undefined : assignment?.product
      )
      .filter((value: SelectValue | undefined) => value !== undefined && value !== null)
      .map(String)
  );
  const selectableProductOptions = productOptions.map((option) => ({
    ...option,
    disabled: selectedMainProductsInOtherRows.has(String(option.value)),
  }));

  useEffect(() => {
    const selectedMainProduct = productOptions.find(
      (option) => option.value === selectedMainProductValue
    );

    form.setFieldValue(['productAssignments', field.name, 'unit'], selectedMainProduct?.unit);

    if (
      previousMainProductValue.current !== undefined &&
      previousMainProductValue.current !== selectedMainProductValue
    ) {
      form.setFieldValue(['productAssignments', field.name, 'products'], []);
    }
    previousMainProductValue.current = selectedMainProductValue;

    if (!selectedMainProduct?.id) {
      setAssignedProductOptions((currentOptions) => ({
        ...currentOptions,
        [field.key]: [],
      }));
      return;
    }

    const loadAssignedProducts = async () => {
      try {
        const products = await productService.getProductByMainProductId(selectedMainProduct.id);
        const options = getAssignedProductOptions(products);

        setAssignedProductOptions((currentOptions) => ({
          ...currentOptions,
          [field.key]: options,
        }));
      } catch {
        const localProducts = await referenceDataSyncService.getPresentations();
        const options = getAssignedProductOptions(
          localProducts.filter((product) => {
            const mainProductId = product.mainProductId ?? product.mainProduct?.id;
            return mainProductId === selectedMainProduct.id;
          })
        );

        setAssignedProductOptions((currentOptions) => ({
          ...currentOptions,
          [field.key]: options,
        }));
      }
    };

    loadAssignedProducts();
  }, [
    field.key,
    field.name,
    form,
    productOptions,
    selectedMainProductValue,
    setAssignedProductOptions,
  ]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-headings-font-color">
          {t('productNumber', { number: index + 1 })}
        </p>
        {fieldsLength > 0 && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              remove(field.name);
              setAssignedProductOptions((currentOptions) => {
                const nextOptions = { ...currentOptions };
                delete nextOptions[field.key];
                return nextOptions;
              });
            }}
            aria-label={t('removeProductAssignment')}
          />
        )}
      </div>

      <Form.Item
        name={[field.name, 'product']}
        label={t('product')}
        rules={[
          requiredRule(t, t('product')),
          {
            validator: (_, value) => {
              if (!value || !selectedMainProductsInOtherRows.has(String(value))) {
                return Promise.resolve();
              }

              return Promise.reject(new Error(t('duplicateMainProductNotAllowed')));
            },
          },
        ]}
      >
        <Select
          placeholder={t('selectProduct')}
          options={selectableProductOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Form.Item
          name={[field.name, 'quantityAssigned']}
          label={t('quantityAssigned')}
          rules={[
            ...(selectedMainProductValue ? [requiredRule(t, t('quantityAssigned'))] : []),
            ...(selectedMainProductValue ? [positiveNumberRule(t, t('quantityAssigned'))] : []),
          ]}
        >
          <InputNumber className="w-full" min={0} precision={4} placeholder={t('enterQuantity')} />
        </Form.Item>

        <Form.Item
          name={[field.name, 'unit']}
          label={t('unit')}
          rules={selectedMainProductValue ? [requiredRule(t, t('unit'))] : []}
        >
          <Select placeholder={t('selectUnit')} options={unitOptions.map((unit) => ({
            label: t(`form:measuringUnits.${unit.value}`),
            value: unit.value,
          }))} showSearch disabled />
        </Form.Item>
      </div>

      <Form.Item
        name={[field.name, 'products']}
        label={t('assignedProducts')}
        extra={t('assignedProductsHelpText')}
        rules={selectedMainProductValue ? [requiredRule(t, t('assignedProducts'))] : []}
      >
        <Select
          mode="multiple"
          placeholder={t('presentationsPlaceholder')}
          options={assignedProductOptions[field.key] ?? []}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>
    </div>
  );
};

const AddBasketForm: React.FC<AddBasketFormProps> = ({
  form,
  // basket,
  handleCancel,
  isModal,
  getConfirmation,
  isBasketModalOpen,
  isEditDataLoading,
}) => {
  const { t } = useTranslation(['basket', 'form']);
  const logoValue = Form.useWatch('logoVal', form);
  const [productOptions, setProductOptions] = useState<MainProductOption[]>([]);
  const [assignedProductOptions, setAssignedProductOptions] = useState<
    Record<number, ProductOption[]>
  >({});

  const getAllMainProductData = async () => {
    try {
      const allMainProducts = await referenceDataSyncService.getMainProducts();
      setProductOptions(getMainProductOptions(allMainProducts));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  useEffect(() => {
    if (isBasketModalOpen === false) return;

    getAllMainProductData();
  }, [isBasketModalOpen]);

  return (
    <>
      {!isModal && (
        <div className="text-center">
          <FormTitle label={'Add Basket'} />
        </div>
      )}

      {isEditDataLoading && (
        <div className="flex justify-center">
          <Spin />
        </div>
      )}

      <Form
        layout="vertical"
        form={form}
        name="addBasketForm"
        onFinish={getConfirmation}
        className="mb-4 mt-2 w-full space-y-4 text-left"
      >
        <FormField
          name="name"
          label={t('basketName')}
          rules={[
            requiredRule(t, t('basketName')),
            noSpacesOnlyRule(t, t('basketName')),
            minLengthRule(t, t('basketName'), 3),
            maxLengthRule(t, t('basketName'), 80),
            regexRule(t, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER),
          ]}
          placeholder={t('basketNameFieldPlaceholder')}
          disabled={false}
          type={FormFieldTypeEnum.INPUT}
          isRequired={true}
          allowLeadingTrailingSpaces={true}
          useCustomRules={true}
        />
        <Form.Item name="id" label="Basket ID" hidden>
          <Input hidden />
        </Form.Item>
        <FormTextArea
          name="description"
          label={t('description')}
          rules={[
            noSpacesOnlyRule(t, t('description')),
            minLengthRule(t, t('description'), 3),
            maxLengthRule(t, t('description'), 200),
            regexRule(t, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER),
          ]}
          placeholder={t('descriptionFieldPlaceholder')}
          disabled={false}
          isRequired={false}
        // allowLeadingTrailingSpaces={true}
        // maxLength={200}
        />

        <div className="space-y-3 border-t border-gray-200 pt-4">
          <div>
            <h3 className="text-base font-semibold text-headings-font-color">
              {t('productAssignment')}
            </h3>
            <p className="text-sm font-medium text-font-color">
              {t('productAssignmentDescription')}
            </p>
          </div>

          <Form.List name="productAssignments">
            {(fields, { add, remove }) => (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <ProductAssignmentItem
                    key={field.key}
                    field={field}
                    index={index}
                    productOptions={productOptions}
                    assignedProductOptions={assignedProductOptions}
                    setAssignedProductOptions={setAssignedProductOptions}
                    remove={remove}
                    fieldsLength={fields.length}
                    t={t}
                  />
                ))}

                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
                  {t('addAnotherProduct')}
                </Button>
              </div>
            )}
          </Form.List>
        </div>

        <FormAttachment
          name="type"
          label={t('basketLogo')}
          isRequired={true}
          form={form}
          maxCount={1}
          fileTypes={[FileTypeEnum.PNG, FileTypeEnum.JPEG, FileTypeEnum.JPG]}
          isDisabled={false}
          validationMessage={t('basketLogoFieldRequired')}
          initialFiles={logoValue ? logoValue : []}
        />
        <Form.Item name="logoVal" label="Basket Logo" hidden>
          <Input hidden />
        </Form.Item>
        {!isModal && (
          <div className="mx-auto mt-6 flex items-center justify-between gap-6">
            <FormButton
              label={t('cancel')}
              type="button"
              disabled={false}
              onClick={handleCancel}
              className="btn-default w-1/2 rounded-2xl"
            />
            <FormButton
              label={t('submit')}
              type="button"
              disabled={false}
              onClick={getConfirmation}
              className="btn-primary w-1/2 rounded-2xl"
            />
          </div>
        )}
      </Form>
    </>
  );
};

export default AddBasketForm;
